import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FONTS } from '../../styles/tokens';
import { KEYCLOAK_CONFIG } from '../../auth/keycloak';

interface Colors {
  text: string;
  textSecondary: string;
  card: string;
  border: string;
}

interface AppRowMenuProps {
  clientId: string;
  rootUrl: string | null;
  colors: Colors;
  onViewDetails: () => void;
  onCopied: (message: string) => void;
}

interface Position { top: number; left: number }

/**
 * Per-row actions kebab menu. Renders the popover via a portal so the
 * surrounding table's `overflow: hidden` doesn't clip it. Positions
 * itself flush with the right edge of the trigger button.
 */
export function AppRowMenu(props: AppRowMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const stop = (event: React.MouseEvent): void => event.stopPropagation();

  const reposition = useCallback((): void => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 6, left: rect.right - MENU_WIDTH });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent): void => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      const target = event.target as Node;
      if (trigger && trigger.contains(target)) return;
      if (popover && popover.contains(target)) return;
      setOpen(false);
    };
    const onReflow = (): void => reposition();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open, reposition]);

  const launchUrl = props.rootUrl ?? '';
  const themePreviewUrl = buildThemePreviewUrl(props.clientId, launchUrl);

  return (
    <span onClick={stop}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label="App actions"
        aria-haspopup="menu"
        aria-expanded={open}
        style={triggerStyle(hover, open, props.colors)}
      >
        <KebabGlyph />
      </button>
      {open && position && createPortal(
        <MenuPopover
          ref={popoverRef} position={position} colors={props.colors}
          launchUrl={launchUrl}
          themePreviewUrl={themePreviewUrl}
          onClose={() => setOpen(false)}
          onCopyClientId={async () => {
            setOpen(false);
            try { await navigator.clipboard.writeText(props.clientId); props.onCopied(`Copied ${props.clientId}`); }
            catch { props.onCopied('Copy failed — clipboard unavailable'); }
          }}
          onViewDetails={() => { setOpen(false); props.onViewDetails(); }}
        />,
        document.body,
      )}
    </span>
  );
}

/** Inline SVG for the kebab trigger. Three dots — vertically centered. */
function KebabGlyph(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ display: 'block' }}>
      <circle cx="8" cy="3" r="1.35" fill="currentColor" />
      <circle cx="8" cy="8" r="1.35" fill="currentColor" />
      <circle cx="8" cy="13" r="1.35" fill="currentColor" />
    </svg>
  );
}

const MENU_WIDTH = 240;

/** Popover body rendered via portal so it can escape any ancestor overflow clip. */
const MenuPopover = React.forwardRef<HTMLDivElement, {
  position: Position;
  colors: Colors;
  launchUrl: string;
  themePreviewUrl: string;
  onClose: () => void;
  onCopyClientId: () => void;
  onViewDetails: () => void;
}>((props, ref) => (
  <div
    ref={ref}
    role="menu"
    style={{
      position: 'fixed',
      top: `${props.position.top}px`,
      left: `${Math.max(8, props.position.left)}px`,
      width: `${MENU_WIDTH}px`,
      background: props.colors.card,
      border: `1px solid ${props.colors.border}`,
      borderRadius: '10px',
      boxShadow: '0 16px 40px rgba(0,0,0,0.36)',
      zIndex: 1000,
      padding: '6px',
    }}
  >
    <SectionLabel colors={props.colors}>Open</SectionLabel>
    <MenuLink colors={props.colors} href={props.launchUrl || undefined} external onAfterClick={props.onClose}>
      Launch app
    </MenuLink>
    <MenuLink colors={props.colors} href={props.themePreviewUrl} external onAfterClick={props.onClose}>
      Preview login theme
    </MenuLink>
    <Divider colors={props.colors} />
    <MenuButton colors={props.colors} onClick={props.onCopyClientId}>Copy client ID</MenuButton>
    <MenuButton colors={props.colors} onClick={props.onViewDetails}>View details</MenuButton>
  </div>
));
MenuPopover.displayName = 'MenuPopover';

/** Subtle section caption above a group. */
function SectionLabel({ colors, children }: { colors: Colors; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{
      padding: '8px 10px 4px',
      fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.18em',
      color: colors.textSecondary, textTransform: 'uppercase',
    }}>
      {children}
    </div>
  );
}

/** Horizontal rule between groups. */
function Divider({ colors }: { colors: Colors }): React.JSX.Element {
  return <div style={{ height: '1px', background: colors.border, margin: '6px 4px' }} />;
}

/** Hoverable anchor-based menu item (opens in new tab for external links). */
function MenuLink(props: { colors: Colors; children: React.ReactNode; href: string | undefined; external?: boolean; onAfterClick: () => void }): React.JSX.Element {
  const [hover, setHover] = useState(false);
  const disabled = !props.href;
  const body = (
    <>
      <span>{props.children}</span>
      {props.external && <ExternalArrow active={hover && !disabled} />}
    </>
  );
  if (!props.href) {
    return <span style={itemStyle(props.colors, hover, true)}>{body}</span>;
  }
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={props.onAfterClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...itemStyle(props.colors, hover, false), textDecoration: 'none' }}
    >
      {body}
    </a>
  );
}

/** Hoverable button-based menu item. */
function MenuButton(props: { colors: Colors; children: React.ReactNode; onClick: () => void }): React.JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={props.onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={itemStyle(props.colors, hover, false)}
    >
      {props.children}
    </button>
  );
}

/** Tiny "opens in new tab" glyph right-aligned on external items. */
function ExternalArrow({ active }: { active: boolean }): React.JSX.Element {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ opacity: active ? 1 : 0.55 }}>
      <path d="M3 1h6v6M9 1L1.5 8.5" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/** Shared trigger-button styling. */
function triggerStyle(hover: boolean, open: boolean, colors: Colors): React.CSSProperties {
  const active = hover || open;
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '30px', height: '30px',
    border: 'none',
    background: active ? 'rgba(1,169,237,0.14)' : 'transparent',
    color: active ? '#01A9ED' : colors.textSecondary,
    cursor: 'pointer', borderRadius: '7px',
    transition: 'background-color 140ms ease, color 140ms ease',
  };
}

/** Shared menu-row styling. */
function itemStyle(colors: Colors, hover: boolean, disabled: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '10px',
    width: '100%', textAlign: 'left',
    padding: '9px 10px', borderRadius: '7px',
    border: 'none',
    background: hover && !disabled ? 'rgba(1,169,237,0.10)' : 'transparent',
    color: disabled ? colors.textSecondary : hover ? '#01A9ED' : colors.text,
    opacity: disabled ? 0.55 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: FONTS.body, fontSize: '13px', fontWeight: 500,
    transition: 'background-color 120ms ease, color 120ms ease',
  };
}

/**
 * Build the Keycloak login URL for a given client. Opening this in a new
 * tab shows the themed login page the way real users would see it.
 * `prompt=login` forces the form to render even if the user has a session.
 */
function buildThemePreviewUrl(clientId: string, redirectUri: string): string {
  const base = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/auth`;
  const fallback = `${window.location.origin}/`;
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid',
    redirect_uri: redirectUri || fallback,
    prompt: 'login',
  });
  return `${base}?${params.toString()}`;
}
